import {
  type EnvironmentId,
  type ModelSelection,
  type ProviderInteractionMode,
  RuntimeMode,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { useCallback } from "react";
import { readEnvironmentApi } from "../../environmentApi";
import { type Thread } from "../../types";
import { newCommandId } from "~/lib/utils";

export function usePersistThreadSettingsForNextTurn(input: {
  readonly environmentId: EnvironmentId;
  readonly serverThread: Thread | undefined;
}): (settings: {
  readonly threadId: ThreadId;
  readonly createdAt: string;
  readonly modelSelection?: ModelSelection;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
}) => Promise<void> {
  return useCallback(
    async (settings) => {
      if (!input.serverThread) {
        return;
      }
      const api = readEnvironmentApi(input.environmentId);
      if (!api) {
        return;
      }

      if (
        settings.modelSelection !== undefined &&
        (settings.modelSelection.model !== input.serverThread.modelSelection.model ||
          settings.modelSelection.provider !== input.serverThread.modelSelection.provider ||
          JSON.stringify(settings.modelSelection.options ?? null) !==
            JSON.stringify(input.serverThread.modelSelection.options ?? null))
      ) {
        await api.orchestration.dispatchCommand({
          type: "thread.meta.update",
          commandId: newCommandId(),
          threadId: settings.threadId,
          modelSelection: settings.modelSelection,
        });
      }

      if (settings.runtimeMode !== input.serverThread.runtimeMode) {
        await api.orchestration.dispatchCommand({
          type: "thread.runtime-mode.set",
          commandId: newCommandId(),
          threadId: settings.threadId,
          runtimeMode: settings.runtimeMode,
          createdAt: settings.createdAt,
        });
      }

      if (settings.interactionMode !== input.serverThread.interactionMode) {
        await api.orchestration.dispatchCommand({
          type: "thread.interaction-mode.set",
          commandId: newCommandId(),
          threadId: settings.threadId,
          interactionMode: settings.interactionMode,
          createdAt: settings.createdAt,
        });
      }
    },
    [input],
  );
}
